<script>
import { jsPDF } from 'jspdf';
import RexButton from '@/rex-components/rex-button.vue';
import { formatDate } from '@/utils/time/formatDate';
import { mapState } from 'vuex';
import HeaderImage from '@/assets/icons/Header.png';
import FooterImage from '@/assets/icons/Footer.png';
import Loading from '@/rex-components/rex-loading.vue';
import { notificationSucess, notificationError } from '@/utils/notification';
import { pdfDownloadTimeFormat } from '@/utils/pdfUtils';

export default {
  components: {
    RexButton,
    Loading,
  },
  props: {
    downloadLabel: {
      type: String,
      required: true,
    },
    fileName: {
      type: String,
      required: true,
    },
    downloadVariant: {
      type: String,
      default: 'primary',
    },
    isCustomHeader: {
      type: Boolean,
      default: false,
    },
    isCustomFooter: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: 'rex-pdf-download',
    },
    title: {
      type: String,
      default: '',
    },
    reportPath: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadPdfData: {
      type: Function,
      default: () => {},
    },
    successNotiMsg: {
      type: String,
      default: '',
    },
    fetchApiError: {
      type: Boolean,
      default: false,
    },
    smaller: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: '12rem',
    },
  },
  computed: {
    ...mapState('globalState/settings', ['timezoneoffset']),
    formattedCurrentTime() {
      const currentTime = new Date();
      return formatDate(
        currentTime,
        this.timezoneoffset,
        pdfDownloadTimeFormat,
      );
    },
    isDownloadDisabled() {
      return this.downloadVariant === 'disabled';
    },
    iconWidth() {
      return this.smaller ? '0.5rem' : '0.75rem';
    },
    iconHeight() {
      return this.smaller ? '0.75rem' : '1rem';
    },
  },
  methods: {
    async generatePdf() {
      try {
        const doc = new jsPDF();
        const pageHeight = doc.internal.pageSize.height;
        const footerHeight = 20;

        await this.loadPdfData();

        if (this.isCustomHeader) {
          this.$emit('pdfHeaderGenerate', doc);
        } else {
          const maxX = this.pdfWidth - 10;
          doc.addImage(this.headerImage, 'PNG', 0, 0, this.pdfWidth, 30);
          doc.setFontSize(10);
          doc.setTextColor('#fff');
          doc.setFont('Rakuten Sans UI', 'normal');
          const reportPathPosition = maxX - doc.getTextWidth(this.reportPath);
          doc.text(this.reportPath, reportPathPosition, 8);
          doc.setFontSize(14);
          doc.setFont('Rakuten Sans UI', 'bold');
          const titlePosition = maxX - doc.getTextWidth(this.title);
          doc.text(this.title, titlePosition, 15);
          doc.setFontSize(10);
          doc.setFont('Rakuten Sans UI', 'normal');
          doc.setTextColor('#D1D1D1');
          const dateText = `${this.$t('date')} - ${this.formattedCurrentTime}`;
          const dateTextPosition = maxX - doc.getTextWidth(dateText);
          doc.text(dateText, dateTextPosition, 22);
        }

        //content
        this.$emit('pdfContentGenerate', doc);

        if (this.isCustomFooter) {
          this.$emit('pdfFooterGenerate', doc);
        } else {
          doc.addImage(
            this.footerImage,
            'PNG',
            0,
            pageHeight - footerHeight,
            212,
            footerHeight,
          );
          doc.setFont('Rakuten Sans UI', 'normal');
          doc.setFontSize(10);
          doc.setTextColor('#F59600');
          doc.text(
            this.$t('rakutenCopyRight'),
            75,
            pageHeight - footerHeight + 12,
          );
        }

        // Save the PDF
        if (!this.fetchApiError) {
          doc.save(this.fileName);
          notificationSucess(
            this.successNotiMsg || this.$t('pdfDownloadSuccMsg'),
          );
        } else {
          notificationError(this.$t('errFetchingTheDataMsg'));
        }
      } catch (error) {
        console.log(error.message);
        notificationError(this.$t('pdfDownloadErrMsg'));
      }
    },
  },
  created() {
    this.headerImage = HeaderImage;
    this.footerImage = FooterImage;
    this.pdfWidth = 210;
  },
};
</script>

<template>
  <div ref="pdfContent">
    <div class="rex-pdf-container">
      <slot name="pdf-header" />
      <slot name="pdf-container" />
      <slot name="pdf-footer" />
    </div>
    <RexButton
      :variant="downloadVariant"
      :styling="{ width: width }"
      @onClick="generatePdf"
      class="rex-pdf-download-btn"
      :id="id"
      :class="[
        loading && 'disable-loading-btn',
        smaller && 'small-download-btn',
      ]"
    >
      <Loading v-if="loading" :smallar="true" />
      <span v-else class="pdf-download-text-icon">
        <BaseIcon
          name="download"
          :width="iconWidth"
          :height="iconHeight"
          class="pdf-download-icon"
          :class="isDownloadDisabled && 'pdf-download-icon-disabled'"
        />
        <span>
          {{ downloadLabel }}
        </span>
      </span>
    </RexButton>
  </div>
</template>

<style lang="scss" scoped>
.rex-pdf-download-btn {
  padding: 0rem 0.5rem;
  cursor: pointer;
}
.rex-pdf-container {
  position: absolute;
  top: -1000rem;
  left: -1000rem;
}
.pdf-download-icon {
  margin-right: 0.525rem;
  @include svg-fill($color-brand-accent);
}
.pdf-download-text-icon {
  @include flex-center;
}
.pdf-download-icon-disabled {
  @include svg-fill($button-disabled-text);
}
.small-download-btn {
  height: 1.43rem;
  font-family: $regular !important;
  font-size: 0.75rem;
}
.disable-loading-btn {
  @include disable-btn;
}
</style>
